import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  output,
  signal,
  ViewChild,
} from '@angular/core';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideX } from '@ng-icons/lucide';
import { HlmBadge } from '@spartan-ng/helm/badge';
import { HlmButton } from '@spartan-ng/helm/button';
import { HlmSheet, HlmSheetImports } from '@spartan-ng/helm/sheet';
import type { RowAction } from '../../admin/shared';
import type { CheckinRowVm, JanelaVm } from './checkin-row.vm';

/**
 * Fronteira de apresentação: abaixo dela o painel é bottom sheet (padrão do
 * app, mesmo do `hlm-action-sheet`); acima, diálogo centralizado. `767.98px`
 * e não `767px` para casar com o breakpoint `md` do Bootstrap que o resto da
 * tela usa — a tabela desktop aparece exatamente em 768px.
 */
const CONSULTA_MOBILE = '(max-width: 767.98px)';

/**
 * Detalhes de um ingresso da tela de Check-ins: janelas de entrada, horário do
 * check-in, campos extras e observações.
 *
 * Substitui o `Swal` de "Campos extras" e o amontoado de HTML em string que a
 * tela usava para mostrar janela por janela. Não busca nada nem decide nada:
 * recebe o `CheckinRowVm` já pronto e só desenha — as regras de status,
 * formato e vazio moram no viewmodel.
 *
 * Uma apresentação por viewport, o mesmo conteúdo nas duas: bottom sheet no
 * mobile (`<768px`), diálogo centralizado no desktop. Os dois hosts ficam
 * sempre no template (são invisíveis enquanto fechados) para que `abrir()` não
 * dependa de um ciclo de change detection criar o host da vez.
 *
 * ```html
 * <app-checkin-detalhes #detalhes />
 * <button (click)="detalhes.abrir(row)">Detalhes</button>
 * ```
 */
@Component({
  selector: 'app-checkin-detalhes',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgTemplateOutlet, NgIcon, HlmBadge, HlmButton, ...HlmSheetImports],
  providers: [provideIcons({ lucideX })],
  template: `
    <!--
      Um sheet só, com o lado decidido por um ternário: bottom no mobile,
      RIGHT no desktop. Era um dialog centralizado no desktop, que em tela
      baixa cortava o conteúdo — o painel lateral ocupa a altura inteira e o
      corpo rola, então lista de 15 janelas e notebook de 13" convivem.
      Um ternário só (nunca duas classes/valores lado a lado): a inversão de
      ordem de camada do !important quebra o outro jeito.

      admin-portal-scope repetido no painel: o CDK portaliza o conteúdo para
      fora do DOM do shell, e sem a classe ele perde os tokens do anel.
    -->
    <hlm-sheet [side]="ehMobile() ? 'bottom' : 'right'" (closed)="aoFechar()">
      <hlm-sheet-content
        *hlmSheetPortal
        [class]="
          ehMobile()
            ? 'admin-portal-scope flex max-h-[90vh] flex-col overflow-hidden p-0'
            : 'admin-portal-scope flex h-full flex-col overflow-hidden p-0 sm:max-w-[28rem]'
        "
        [showCloseButton]="false"
      >
        @if (ehMobile()) {
          <!--
            A alça é button e não div: no mobile o sheet ocupa 90vh e o que
            sobra de backdrop fica atrás da barra do navegador (ou do banner de
            "abrir no app"), então clicar fora nem sempre é alcançável e não há
            Esc — sem um alvo explícito o usuário fica preso no painel. O py-3
            dá área de toque ao redor da barrinha de 4px.
          -->
          <button
            type="button"
            class="mx-auto shrink-0 cursor-pointer border-0 bg-transparent px-6 py-3"
            data-testid="fechar-alca"
            aria-label="Fechar"
            (click)="fechar()"
          >
            <span class="block h-[4px] w-[40px] rounded-full bg-border"></span>
          </button>
        }
        <ng-template [ngTemplateOutlet]="corpo" />
      </hlm-sheet-content>
    </hlm-sheet>

    <ng-template #corpo>
      @if (row(); as r) {
        <div data-testid="checkin-detalhes" class="flex min-h-0 flex-col">
          <header
            class="flex shrink-0 items-start justify-between gap-3 border-b border-border px-[1rem] py-3.5"
          >
            <h2 class="m-0 min-w-0 flex-1 font-display text-[0.95rem]/[1.2] font-bold break-words">
              {{ r.nome }}
            </h2>
            <span hlmBadge [status]="r.badgeStatus" class="shrink-0">{{ r.statusLabel }}</span>
            <!--
              Também no mobile: a alça acima resolve o dedo, este X resolve o
              leitor de tela e quem chega por teclado. Tamanho icon (40px) no
              mobile para o alvo de toque, icon-sm no desktop, ponteiro fino.
            -->
            <button
              type="button"
              hlmBtn
              variant="ghost"
              [size]="ehMobile() ? 'icon' : 'icon-sm'"
              class="shrink-0"
              data-testid="fechar"
              aria-label="Fechar"
              (click)="fechar()"
            >
              <ng-icon name="lucideX" />
            </button>
          </header>

          <div class="flex min-h-0 flex-col gap-4 overflow-y-auto px-[1rem] pt-[1rem] pb-[1rem]">
            <section>
              <h3 class="m-0 mb-1.5 text-xs font-semibold tracking-wide text-muted-foreground uppercase">
                Dados
              </h3>
              <dl class="m-0 flex flex-col gap-1">
                @for (campo of r.detalhes; track campo.label) {
                  <div data-testid="dado" class="flex items-baseline justify-between gap-3">
                    <dt class="shrink-0 text-[0.8rem] text-muted-foreground">{{ campo.label }}</dt>
                    <dd class="m-0 text-right text-[0.8rem] break-words">{{ campo.value }}</dd>
                  </div>
                }
              </dl>
            </section>

            <section>
              <h3 class="m-0 mb-1.5 text-xs font-semibold tracking-wide text-muted-foreground uppercase">
                Janelas de entrada
              </h3>
              @if (r.janelas.length === 0) {
                <p data-testid="janelas-vazio" class="m-0 text-[0.8rem] text-muted-foreground">
                  Este lote não tem janela de entrada configurada.
                </p>
              } @else {
                <!-- Lote com janela diária chega com 15+: a lista rola dentro
                     do painel, senão empurra as seções seguintes para fora da
                     tela. A rolagem fica contida no proprio bloco, evita arrastar o painel junto
                     quando a rolagem chega ao fim no mobile. -->
                <ul
                  data-testid="janelas"
                  class="m-0 flex max-h-[15rem] list-none flex-col gap-2 overflow-y-auto overscroll-contain p-0"
                >
                  @for (janela of r.janelas; track $index) {
                    <li
                      data-testid="janela"
                      class="flex items-center justify-between gap-3 rounded-[10px] bg-muted px-3 py-2"
                    >
                      <div class="flex min-w-0 flex-col">
                        <span class="text-[0.8rem] break-words">{{ janela.periodoCurto }}</span>
                        @if (janela.label) {
                          <span class="text-[0.7rem] text-muted-foreground break-words">
                            {{ janela.label }}
                          </span>
                        }
                        <!-- Quem entrou nesta janela e quando. É a mesma
                             informação da mini-tabela do desktop; sem ela o
                             mobile só dizia se a janela estava aberta, e não
                             se alguém passou por ela. -->
                        @if (janela.checkin; as feito) {
                          <span data-testid="janela-checkin" class="text-[0.7rem] text-[#0A7A35]">
                            Check-in {{ feito.horario }}@if (feito.operador) {
                              <span> &middot; {{ feito.operador }}</span>
                            }
                          </span>
                        }
                      </div>
                      <div class="flex shrink-0 flex-col items-end gap-1.5">
                        @if (janela.checkin) {
                          <span hlmBadge status="success">Realizado</span>
                        } @else {
                          <span hlmBadge [status]="janela.badgeStatus">
                            {{ janela.statusLabel }}
                          </span>
                        }
                        <!-- Só na janela que o produtor habilitou E que ainda
                             não tem entrada: é o horário que FALTOU registrar,
                             não uma segunda entrada. Quem decide é o VM
                             (retroativoDisponivel), como todo o resto. -->
                        @if (janela.retroativoDisponivel) {
                          <button
                            type="button"
                            hlmBtn
                            variant="outline"
                            size="sm"
                            data-testid="janela-retroativo"
                            (click)="pedirRetroativo(janela)"
                          >
                            Check-in retroativo
                          </button>
                        }
                      </div>
                    </li>
                  }
                </ul>
              }
            </section>

            <section>
              <h3 class="m-0 mb-1.5 text-xs font-semibold tracking-wide text-muted-foreground uppercase">
                Check-in
              </h3>
              @if (r.presente) {
                <dl class="m-0 flex flex-col gap-1">
                  <div class="flex items-baseline justify-between gap-3">
                    <dt class="shrink-0 text-[0.8rem] text-muted-foreground">Horário</dt>
                    <dd data-testid="checkin-horario" class="m-0 text-right text-[0.8rem]">
                      {{ r.dataCheckinLabel }}
                    </dd>
                  </div>
                  <div class="flex items-baseline justify-between gap-3">
                    <dt class="shrink-0 text-[0.8rem] text-muted-foreground">Operador</dt>
                    <dd
                      data-testid="checkin-operador"
                      class="m-0 text-right text-[0.8rem] break-words"
                    >
                      {{ r.operador }}
                    </dd>
                  </div>
                </dl>
              } @else {
                <p data-testid="checkin-pendente" class="m-0 text-[0.8rem] text-muted-foreground">
                  Ainda não realizado.
                </p>
              }
            </section>

            <section>
              <h3 class="m-0 mb-1.5 text-xs font-semibold tracking-wide text-muted-foreground uppercase">
                Campos extras
              </h3>
              @if (r.temCamposExtras) {
                <dl class="m-0 flex flex-col gap-1">
                  @for (campo of r.camposExtras; track campo.label) {
                    <div
                      data-testid="campo-extra"
                      class="flex items-baseline justify-between gap-3"
                    >
                      <dt class="shrink-0 text-[0.8rem] text-muted-foreground">{{ campo.label }}</dt>
                      <dd class="m-0 text-right text-[0.8rem] break-words">{{ campo.value }}</dd>
                    </div>
                  }
                </dl>
              } @else {
                <!-- Texto idêntico ao do Swal que este painel substitui. -->
                <p
                  data-testid="campos-extras-vazio"
                  class="m-0 text-[0.8rem] text-muted-foreground"
                >
                  Este ingresso não possui campos extras.
                </p>
              }
            </section>

            <!-- Sem observação o viewmodel entrega o traço do orDash: seção
                 inteira some, em vez de mostrar um rótulo com '--'. -->
            @if (r.observacoes !== '--') {
              <section data-testid="observacoes">
                <h3
                  class="m-0 mb-1.5 text-xs font-semibold tracking-wide text-muted-foreground uppercase"
                >
                  Observações
                </h3>
                <p class="m-0 text-[0.8rem] break-words">{{ r.observacoes }}</p>
              </section>
            }
          </div>

          <!-- Fora do corpo rolável e shrink-0: as ações ficam à mão mesmo num
               ingresso com 14 janelas, que empurra o resto para fora da tela.
               No mobile este rodapé é o ÚNICO caminho para reenviar ingresso,
               imprimir QR e etiqueta — o card só oferece Check-in e o '⋯', e o
               '⋯' abre este painel. -->
          @if (acoesDaLinha().length) {
            <footer
              data-testid="acoes"
              class="flex shrink-0 flex-wrap gap-2 border-t border-border bg-card px-[1rem] py-3"
            >
              @for (acao of acoesDaLinha(); track acao.key) {
                <button
                  type="button"
                  hlmBtn
                  size="sm"
                  [variant]="acao.danger ? 'destructive' : 'outline'"
                  class="min-w-[calc(50%-0.25rem)] flex-1"
                  (click)="executar(acao)"
                >
                  {{ acao.label }}
                </button>
              }
            </footer>
          }
        </div>
      }
    </ng-template>
  `,
})
export class CheckinDetalhesComponent {
  // `@ViewChild` e não `viewChild()`: o harness de teste compila os
  // componentes do app em JIT (`@angular/compiler` no src/test.ts, sem o
  // transform do compilador Angular), e em JIT as queries por signal nunca
  // resolvem — `abrir()` estouraria NG0951 em todo teste. O decorator resolve
  // nos dois modos.
  @ViewChild(HlmSheet) private sheet!: HlmSheet;

  /**
   * As mesmas ações do menu da tabela desktop. O painel não decide nada: a
   * disponibilidade por linha continua sendo o `permission` de cada ação, que
   * consulta o viewmodel.
   */
  readonly acoes = input<RowAction<CheckinRowVm>[]>([]);

  /**
   * Pedido de check-in retroativo numa janela específica (GUE-430). O painel
   * não registra nada: quem tem o transporte e a lista para recarregar é a
   * página — aqui só sai QUAL janela de QUAL linha.
   */
  readonly retroativoSolicitado = output<{ row: CheckinRowVm; janela: JanelaVm }>();

  /** Linha em exibição; `null` enquanto nunca foi aberto. */
  readonly row = signal<CheckinRowVm | null>(null);

  /**
   * `camposExtras` fica de fora: é a ação que ABRE este painel, e repeti-la
   * aqui seria um botão que não leva a lugar nenhum.
   */
  readonly acoesDaLinha = computed<RowAction<CheckinRowVm>[]>(() => {
    const r = this.row();
    if (!r) return [];
    return this.acoes().filter((acao) => {
      if (acao.key === 'camposExtras') return false;
      const permitida = acao.permission;
      if (permitida === undefined) return true;
      return typeof permitida === 'function' ? permitida(r) : permitida;
    });
  });

  /**
   * Mesma regra do `executar`: fecha antes, porque o modal do retroativo é um
   * diálogo e um diálogo atrás do sheet não recebe clique.
   */
  protected pedirRetroativo(janela: JanelaVm): void {
    const r = this.row();
    if (!r) return;
    this.fechar();
    this.retroativoSolicitado.emit({ row: r, janela });
  }

  /**
   * Fecha ANTES de executar: quase toda ação abre um Swal ou o gerenciador da
   * impressora, e um diálogo atrás do sheet não recebe clique.
   */
  executar(acao: RowAction<CheckinRowVm>): void {
    const r = this.row();
    if (!r) return;
    this.fechar();
    acao.action(r);
  }

  /**
   * `matchMedia` e não `innerWidth`: o CDK não traz `BreakpointObserver` nesta
   * versão (`@angular/cdk/layout` não existe no pacote) e o listener nativo já
   * resolve rotação de tela sem ouvir `resize` a cada pixel.
   */
  private readonly mql =
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(CONSULTA_MOBILE)
      : null;

  /** Escrito por callback assíncrono (`change` do matchMedia) e lido no template. */
  readonly ehMobile = signal(this.mql?.matches ?? false);

  /**
   * Se o painel está aberto em ALGUMA das duas apresentações. Sincronizado
   * pelo output `closed` dos dois hosts porque o usuário também fecha por
   * fora (clique no backdrop, Esc) — sem isso, uma troca de viewport depois
   * reabriria um painel que já tinha sido dispensado.
   */
  private readonly aberto = signal(false);

  constructor() {
    const mql = this.mql;
    if (!mql || typeof mql.addEventListener !== 'function') return;

    const aoMudar = (e: MediaQueryListEvent) => this.trocarApresentacao(e.matches);
    mql.addEventListener('change', aoMudar);
    inject(DestroyRef).onDestroy(() => mql.removeEventListener('change', aoMudar));
  }

  /** Abre o painel na apresentação da viewport atual. */
  abrir(row: CheckinRowVm): void {
    this.row.set(row);
    // Releitura em vez de confiar só no listener: cobre o caso de a viewport
    // ter mudado antes de o componente existir (ex.: rotação com o painel
    // fechado em navegador que não dispara `change` para quem não ouvia).
    this.ehMobile.set(this.mql?.matches ?? this.ehMobile());
    this.aberto.set(true);
    this.sheet.open();
  }

  fechar(): void {
    this.aberto.set(false);
    this.sheet.close();
  }

  protected aoFechar(): void {
    this.aberto.set(false);
  }

  /**
   * Troca de viewport com o painel aberto: como agora é UM host só, basta o
   * `side` mudar — não há mais fechar-e-reabrir, que piscava na transição.
   */
  private trocarApresentacao(mobile: boolean): void {
    this.ehMobile.set(mobile);
  }
}
